/**
 * Matemática de calendario (ADR 0047).
 *
 * `imprenta` usa `date-fns` para esto. Acá va puro: son unas treinta líneas
 * contra un paquete entero en el bundle, y **son treinta líneas con bordes**
 * —fin de mes, cambio de año, la semana que arranca lunes— que conviene tener
 * testeadas y no delegadas.
 *
 * **Todo en hora local**, a diferencia del resto del sistema, que persiste en
 * UTC. Acá es correcto: un calendario dice en qué casilla cae una entrega para
 * quien lo mira, y `fecha_pactada_entrega` es un `date` sin hora — tratarlo en
 * UTC correría las entregas un día para el lado de Argentina.
 */

/** Lunes a domingo, como `imprenta` (`weekStartsOn: 1`). */
export const DIAS_DE_LA_SEMANA = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"] as const;

/** Medianoche local del día. Sirve para comparar sin que la hora moleste. */
export function aDia(fecha: Date): Date {
  return new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());
}

export function mismoDia(a: Date, b: Date): boolean {
  return aDia(a).getTime() === aDia(b).getTime();
}

/**
 * El lunes de la semana que contiene a `fecha`.
 *
 * `getDay()` devuelve 0 para domingo, así que el domingo hay que retroceder
 * **seis** días y no cero: sin ese ajuste, cada domingo abre su propia semana.
 */
export function inicioDeSemana(fecha: Date): Date {
  const dia = aDia(fecha);
  const diaSemana = dia.getDay();
  const retroceso = diaSemana === 0 ? 6 : diaSemana - 1;
  dia.setDate(dia.getDate() - retroceso);
  return dia;
}

export function sumarDias(fecha: Date, dias: number): Date {
  const d = aDia(fecha);
  d.setDate(d.getDate() + dias);
  return d;
}

/**
 * Corre `meses` desde `fecha`, quedando siempre en el día 1.
 *
 * Se fija el día en 1 **antes** de mover el mes: `new Date(2026, 0, 31)` más un
 * mes daría 3 de marzo, porque febrero no tiene 31. Como la grilla siempre
 * arranca del día 1, fijarlo primero evita ese salto.
 */
export function sumarMeses(fecha: Date, meses: number): Date {
  return new Date(fecha.getFullYear(), fecha.getMonth() + meses, 1);
}

/**
 * Las casillas de la grilla.
 *
 * En vista mensual va del lunes de la semana del día 1 al domingo de la semana
 * del último día — por eso una grilla de mes casi siempre muestra días del mes
 * anterior y del siguiente, y por eso `pertenece` existe: para pintarlos
 * apagados en vez de ocultarlos y dejar la grilla coja.
 */
export function diasDeLaGrilla(
  referencia: Date,
  vista: "mes" | "semana",
): { fecha: Date; pertenece: boolean }[] {
  const mes = referencia.getMonth();

  const desde =
    vista === "semana"
      ? inicioDeSemana(referencia)
      : inicioDeSemana(new Date(referencia.getFullYear(), mes, 1));

  const casillas = vista === "semana" ? 7 : 42;
  const dias: { fecha: Date; pertenece: boolean }[] = [];
  for (let i = 0; i < casillas; i += 1) {
    const fecha = sumarDias(desde, i);
    dias.push({ fecha, pertenece: vista === "semana" || fecha.getMonth() === mes });
  }

  // Seis semanas fijas en la vista mensual: con un número variable, la grilla
  // cambia de alto al navegar y el contenido salta bajo el cursor.
  return dias;
}

/** `2026-08-12` (lo que devuelve la API) a un `Date` LOCAL, no UTC. */
export function desdeIso(iso: string): Date | null {
  const partes = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!partes) return null;
  return new Date(Number(partes[1]), Number(partes[2]) - 1, Number(partes[3]));
}

const MESES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
] as const;

export function tituloDelPeriodo(referencia: Date, vista: "mes" | "semana"): string {
  if (vista === "mes") {
    return `${MESES[referencia.getMonth()]} ${referencia.getFullYear()}`;
  }
  const desde = inicioDeSemana(referencia);
  const hasta = sumarDias(desde, 6);
  const dm = (d: Date) => `${d.getDate()} ${MESES[d.getMonth()]?.slice(0, 3)}`;
  return `${dm(desde)} — ${dm(hasta)} ${hasta.getFullYear()}`;
}

/**
 * Lunes a domingo empieza la grilla, pero `getDay()` empieza en domingo. Esta
 * tabla va en el orden de `getDay()`, no en el de la grilla — mezclarlos corre
 * el nombre del día un lugar y nadie lo nota hasta un domingo.
 */
const DIAS_LARGOS = [
  "domingo",
  "lunes",
  "martes",
  "miércoles",
  "jueves",
  "viernes",
  "sábado",
] as const;

/**
 * `lunes 24 de agosto`, el título del diálogo del día de `imprenta`
 * (`EEEE d 'de' MMMM`). Sin año a propósito: el año ya está en el encabezado
 * del mes que se está mirando.
 */
export function etiquetaDelDia(fecha: Date): string {
  return `${DIAS_LARGOS[fecha.getDay()]} ${fecha.getDate()} de ${MESES[fecha.getMonth()]}`;
}
